import { headingLevels } from './heading-levels'

const WORD_NAMESPACE = 'urn:schemas-microsoft-com:office:word'
const HEADING_RATIO = 1.15
const MIN_SPACING_PT = 4

const POINTS_PER_UNIT: Record<string, number> = {
  pt: 1,
  px: 0.75,
  cm: 72 / 2.54,
  mm: 72 / 25.4,
  in: 72,
}

type Rules = Map<string, Map<string, string>>

const parseDeclarations = (css: string) => {
  const declarations = new Map<string, string>()
  css.split(';').forEach(declaration => {
    const colon = declaration.indexOf(':')
    if (colon < 0) return
    const property = declaration.slice(0, colon).trim().toLowerCase()
    declarations.set(property, declaration.slice(colon + 1).trim())
  })
  return declarations
}

const readRules = (doc: Document): Rules => {
  const rules: Rules = new Map()
  const css = [...doc.querySelectorAll('style')]
    .map(style => style.textContent ?? '')
    .join('\n')
    .replace(/\/\*[\s\S]*?\*\/|<!--|-->/g, '')

  for (const [, selectors, body] of css.matchAll(/([^{}@]+)\{([^}]*)\}/g)) {
    selectors?.split(',').forEach(selector => {
      rules.set(selector.trim().toLowerCase(), parseDeclarations(body ?? ''))
    })
  }
  return rules
}

const toPoints = (value: string | undefined) => {
  const match = value?.match(/^(-?[\d.]+)\s*(pt|px|cm|mm|in)?$/i)
  if (!match?.[1]) return undefined
  return (
    Number(match[1]) * (POINTS_PER_UNIT[match[2]?.toLowerCase() ?? 'pt'] ?? 1)
  )
}

/** Inline style wins over the rule for the element's tag and Word class. */
const styleOf = (element: Element, rules: Rules, property: string) => {
  const inline = parseDeclarations(element.getAttribute('style') ?? '')
  const tag = element.tagName.toLowerCase()
  const className = element.getAttribute('class')?.toLowerCase()
  const fromRule = (declarations?: Map<string, string>) => {
    if (!declarations) return undefined
    if (declarations.has(property)) return declarations.get(property)

    const side = property.match(/^margin-(top|bottom)$/)?.[1]
    const shorthand = side && declarations.get('margin')?.split(/\s+/)
    if (!shorthand) return undefined
    return side === 'top' ? shorthand[0] : shorthand[2] ?? shorthand[0]
  }

  return (
    fromRule(inline) ??
    fromRule(className ? rules.get(`${tag}.${className}`) : undefined) ??
    fromRule(rules.get(tag))
  )
}

const isListItem = (element: Element) =>
  /mso-list:/i.test(element.getAttribute('style') ?? '')

const isEmpty = (element: Element) =>
  !element.textContent?.trim() && !element.querySelector('img')

/**
 * The size of each piece of text comes from the nearest inline font-size
 * between it and its paragraph, falling back to the paragraph's own size.
 */
const textSizes = (paragraph: Element, rules: Rules, fallback: number) => {
  const paragraphSize =
    toPoints(styleOf(paragraph, rules, 'font-size')) ?? fallback
  const sizes: { size: number; length: number }[] = []
  const walker = paragraph.ownerDocument.createTreeWalker(
    paragraph,
    NodeFilter.SHOW_TEXT
  )

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const length = node.textContent?.trim().length ?? 0
    if (!length) continue

    let size: number | undefined
    for (
      let element = node.parentElement;
      element && element !== paragraph && size === undefined;
      element = element.parentElement
    ) {
      size = toPoints(
        parseDeclarations(element.getAttribute('style') ?? '').get('font-size')
      )
    }
    sizes.push({ size: size ?? paragraphSize, length })
  }
  return sizes
}

const dominantSize = (sizes: { size: number; length: number }[]) => {
  const weight = new Map<number, number>()
  sizes.forEach(({ size, length }) =>
    weight.set(size, (weight.get(size) ?? 0) + length)
  )
  return [...weight].sort((a, b) => b[1] - a[1])[0]?.[0]
}

const promoteHeadings = (paragraphs: Element[], rules: Rules) => {
  const fallback = toPoints(rules.get('p.msonormal')?.get('font-size')) ?? 11
  const sizes = new Map(
    paragraphs.map(p => [p, textSizes(p, rules, fallback)] as const)
  )
  const bodySize = dominantSize([...sizes.values()].flat())
  if (!bodySize) return

  const headings = paragraphs.flatMap(paragraph => {
    const paragraphSizes = sizes.get(paragraph) ?? []
    const smallest = Math.min(...paragraphSizes.map(({ size }) => size))
    return paragraphSizes.length && smallest >= bodySize * HEADING_RATIO
      ? [{ paragraph, size: smallest }]
      : []
  })
  const levels = headingLevels(headings.map(({ size }) => size))

  headings.forEach(({ paragraph, size }) => {
    const heading = paragraph.ownerDocument.createElement(
      `h${levels.get(size) ?? 3}`
    )
    heading.innerHTML = paragraph.innerHTML
    paragraph.replaceWith(heading)
  })
}

/**
 * Quill has no paragraph spacing, so Word's space before/after a paragraph
 * becomes an empty line. List items are left adjacent, because Quill only
 * groups Word list paragraphs that are direct siblings.
 */
const addParagraphSpacing = (doc: Document, rules: Rules) => {
  const blocks = [
    ...doc.body.querySelectorAll('p, h1, h2, h3, h4, h5, h6'),
  ].filter(block => !block.closest('td, th, li'))

  blocks.forEach(block => {
    const next = block.nextElementSibling
    if (!next || !blocks.includes(next)) return
    if (isEmpty(block) || isEmpty(next)) return
    if (isListItem(block) && isListItem(next)) return

    const spacing = Math.max(
      toPoints(styleOf(block, rules, 'margin-bottom')) ?? 0,
      toPoints(styleOf(next, rules, 'margin-top')) ?? 0
    )
    if (spacing < MIN_SPACING_PT) return

    const blank = doc.createElement('p')
    blank.appendChild(doc.createElement('br'))
    block.after(blank)
  })
}

/**
 * Word copies paragraph spacing and font sizes into a <style> block and
 * inline styles that Quill never reads. Larger-than-body paragraphs become
 * headings and spaced paragraphs get an empty line between them. The rest of
 * the markup is kept so Quill's own Word handling (lists) still applies.
 *
 * Returns null for any other HTML, which Quill then converts as usual.
 */
export function normalizeWordHtml(html: string): string | null {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  if (doc.documentElement.getAttribute('xmlns:w') !== WORD_NAMESPACE)
    return null

  const rules = readRules(doc)
  const paragraphs = [...doc.body.querySelectorAll('p')].filter(
    p => !p.closest('td, th, li') && !isListItem(p) && !isEmpty(p)
  )
  addParagraphSpacing(doc, rules)
  promoteHeadings(paragraphs, rules)

  return doc.documentElement.outerHTML
}
